/**
 * fetch() with short retries for transient upstream failures.
 *
 * The CMS occasionally times out, returns 5xx under load, or resets reused
 * keep-alive sockets (ECONNRESET — observed during full builds). Since the
 * fetcher contract now throws on such failures — so ISR keeps stale pages
 * instead of caching 404s — a single blip would fail a build prerender or a
 * first-visit render. Note that Next's own "attempt x of 3" static-generation
 * retry only covers page *timeouts*; a thrown error aborts the build
 * immediately, so this fetch-level retry is the only defense against
 * connection resets. Three retries with growing backoff absorb blips without
 * hiding real outages: after the attempts are exhausted the failure still
 * propagates.
 *
 * Only use for idempotent GET requests.
 */

const RETRYABLE_STATUS = new Set([500, 502, 503, 504]);
const RETRY_DELAYS_MS = [500, 1500, 4000];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function fetchWithRetry(
  url: string | URL,
  options?: RequestInit,
): Promise<Response> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) {
      await wait(RETRY_DELAYS_MS[attempt - 1]);
    }

    try {
      const res = await fetch(url, options);
      if (
        !RETRYABLE_STATUS.has(res.status) ||
        attempt === RETRY_DELAYS_MS.length
      ) {
        return res;
      }
      lastError = new Error(`Retryable upstream status ${res.status}`);
    } catch (error) {
      lastError = error;
      if (attempt === RETRY_DELAYS_MS.length) {
        throw error;
      }
    }
  }

  // Unreachable: the loop always returns or throws on the last attempt.
  throw lastError;
}
